import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  CourseClassroomSimpleResponse,
  CourseClassroomStateEnum,
  CourseDefinitionResponse,
} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {Pill} from 'src/design/atoms/Pill';
import {CourseClassroomStatePill} from 'src/shared/constants/PillConstants';
import {Spinner} from 'src/design/atoms/Spinner';
import {CourseClassroomStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useGetCourseClassroomListV2Query} from 'src/redux/queries/courseClassroom';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ViewCourseClassroomsProps {
  course: CourseDefinitionResponse;
}
const ViewCourseClassrooms: React.FC<ViewCourseClassroomsProps> = ({
  course,
}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<CourseClassroomSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'internalIdentifier',
        size: 200,
        header: 'Class',
      },
      {
        accessorKey: 'classroomAt',
        header: 'Class Date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        accessorKey: 'state',
        header: 'Status',
        width: 500,
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseClassroomStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill
            {...CourseClassroomStatePill(
              cell.getValue<CourseClassroomStateEnum>()
            )}
          />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'course-def-classrooms',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'classroomAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseClassroomListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
      courseDefinitionId: course.id,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item
            onClick={() =>
              navigate(
                PathUtility.TrainingCourseClassroomView(
                  String(course.id),
                  String(row.original.id),
                  'roster'
                )
              )
            }>
            View Classroom
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <Group justify="space-between" gap={3}>
          <PermissionGuard
            module={ModuleEnum.Training}
            operation={PermissionOperationEnum.Create}>
            <CreateButton
              onClick={handleCreateClass}
              loading={isLoading}
              disabled={course.locked}>
              Create Class
            </CreateButton>
          </PermissionGuard>
        </Group>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const handleCreateClass = () => {
    navigate(PathUtility.TrainingCourseClassroomCreate(String(course.id)));
  };

  return !isLoading ? <>{table}</> : <Spinner loadingText="Loading...." />;
};

export default ViewCourseClassrooms;
